import { projectId, publicAnonKey } from '../../../utils/supabase/info';
import type { Access } from '../../../supabase/functions/_shared/plan-access';
import { planLimitDetail, planLimitText } from './plan-texts';

const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-df8bdc4f`;

// Validate environment configuration
if (!projectId || !publicAnonKey) {
  console.error('🔴 CRITICAL: Supabase credentials missing!', { projectId, publicAnonKey: publicAnonKey ? 'SET' : 'MISSING' });
}

console.log('🔧 API Configuration:', {
  projectId,
  API_BASE,
  publicAnonKeyExists: !!publicAnonKey
});

export interface ApiResponse<T = any> {
  success?: boolean;
  error?: string;
  [key: string]: any;
}

// Helper to normalize PostgreSQL order data (snake_case) to frontend format (camelCase)
function normalizeOrder(order: any): any {
  if (!order) return null;

  const normalized = {
    ...order,
    // Map snake_case to camelCase
    orderNumber: order.order_number || order.orderNumber,
    createdAt: order.created_at || order.createdAt,
    updatedAt: order.updated_at || order.updatedAt,
    customerId: order.customer_id || order.customerId,
    deliveryDate: order.delivery_date || order.deliveryDate,
    deliveryAddress: order.delivery_address || order.deliveryAddress,
    paymentMethod: order.payment_method || order.paymentMethod,
    paymentStatus: order.payment_status || order.paymentStatus,
    amountPaid: order.amount_paid || order.amountPaid,
    homeDelivery: order.home_delivery || order.homeDelivery,
    splitPayments: order.split_payments || order.splitPayments,
    isCancelled: order.is_cancelled || order.isCancelled,
    cancelReason: order.cancel_reason || order.cancelReason,
    cancelledAt: order.cancelled_at || order.cancelledAt,
    cancelledBy: order.cancelled_by || order.cancelledBy,
    // Extract customer info from JOIN
    customerName: order.customers?.name || order.customerName || '',
    customerPhone: order.customers?.phone || order.customerPhone || '',
    customerAddress: order.customers?.address || order.customerAddress || '',
  };

  // Log normalization for debugging (only first order)
  if (order.order_number && !order.orderNumber) {
    console.log('🔄 Order normalized:', {
      from: { order_number: order.order_number, created_at: order.created_at, customer: order.customers?.name },
      to: { orderNumber: normalized.orderNumber, createdAt: normalized.createdAt, customerName: normalized.customerName }
    });
  }

  return normalized;
}

// Currency formatting helper
export function formatCurrency(amount: number, settings?: any): string {
  const currency = settings?.currency || 'TRY';
  const currencyPosition = settings?.currencyPosition || 'right';

  const symbols: any = {
    TRY: '₺',
    USD: '$',
    EUR: '€'
  };

  const symbol = symbols[currency] || '₺';
  const formatted = amount.toFixed(2);

  if (currencyPosition === 'left') {
    return `${symbol}${formatted}`;
  } else {
    return `${formatted}${symbol}`;
  }
}

// Ensures a header value is a valid Latin-1 ByteString (code points 0-255, no CR/LF/NUL)
function safeHeader(value: string): string {
  return value.replace(/[^\x00-\xFF]/g, '').replace(/[\r\n\0]/g, '');
}

export async function fetchApi<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  // Get tenantId from localStorage
  const tenantId = localStorage.getItem('tenantId');
  // Get language from localStorage for error messages (use appLanguage - same as LanguageContext)
  const language = localStorage.getItem('appLanguage') || 'tr';

  try {
    // Sanitize tenantId to prevent non-ASCII characters
    const sanitizedTenantId = tenantId ? tenantId.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9-_]/g, '') : undefined;
    // Sanitize language code to ensure it's ISO-8859-1 compatible (only a-z, A-Z, 0-9, hyphen)
    const sanitizedLanguage = language.toLowerCase().replace(/[^a-z0-9-]/g, '');

    const url = `${API_BASE}${endpoint}`;

    // Branch ID (opsiyonel — kasiyerin/şoförün şubesi)
    const rawBranchId = localStorage.getItem('branchId') || '';
    const branchId = rawBranchId.replace(/[^\x00-\xFF]/g, '').replace(/[\r\n\0]/g, '');

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${safeHeader(publicAnonKey)}`,
        ...(sanitizedTenantId && { 'X-Tenant-ID': safeHeader(sanitizedTenantId) }),
        ...(() => { const st = localStorage.getItem('sessionToken'); return st ? { 'X-Session-Token': safeHeader(st) } : {}; })(),
        ...(branchId && { 'X-Branch-ID': safeHeader(branchId) }),
        'Accept-Language': safeHeader(sanitizedLanguage),
        ...options.headers,
      },
    });

    // Check if response is actually JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('❌ API Error - Non-JSON response:', {
        endpoint,
        status: response.status,
        contentType,
        body: text.substring(0, 200), // First 200 chars
      });
      throw new Error(`API returned non-JSON response: ${text.substring(0, 100)}`);
    }

    const data = await response.json();

    if (!response.ok) {
      console.error('❌ API Error:', {
        endpoint,
        status: response.status,
        statusText: response.statusText,
        data,
      });

      // 402 paket sınırı / abonelik kilidi: ham kod yerine okunur metin; kod ve özellik hatada kalır.
      // ("Satın al" penceresini session-fetch'teki 402 izleyicisi açar.)
      if (response.status === 402) {
        const detail = planLimitDetail(data);
        if (detail) {
          const planError: any = new Error(planLimitText(detail.feature, language, detail.limit).title);
          planError.code = data.error;
          planError.feature = detail.feature;
          planError.limit = detail.limit;
          throw planError;
        }
      }

      // ⚠️ 409 Conflict - expected business logic error (not a technical error)
      if (response.status === 409) {
        console.warn('⚠️ Business Logic Conflict:', data.error);
        throw new Error(data.error || 'İşlem yapılamadı: Çakışma tespit edildi');
      }

      // Check for schema-related errors
      if (data.error && typeof data.error === 'string') {
        if (data.error.includes('schema cache') || data.error.includes('column') || data.error.includes('relationship') || data.error.includes('not-null constraint') || data.error.includes('violates')) {
          // Special message for payments table
          if (data.error.includes('payments')) {
            const schemaError = `
🔴 PAYMENTS TABLOSU HATASI!

${data.error}

⚡ HIZLI ÇÖZÜM:

Eski schema'dan yeni schema'ya geçiş gerekli.

1️⃣ Supabase SQL Editor'a gidin: https://supabase.com/dashboard/project/_/sql

2️⃣ Aşağıdaki SQL kodunu çalıştırın:

\`\`\`sql
-- ESKİ KOLON SİL (payment_method)
ALTER TABLE payments DROP COLUMN IF EXISTS payment_method;

-- YENİ KOLONLARI EKLE
ALTER TABLE payments ADD COLUMN IF NOT EXISTS method TEXT NOT NULL DEFAULT 'cash';
ALTER TABLE payments ADD COLUMN IF NOT EXISTS notes TEXT;
ALTER TABLE payments ALTER COLUMN id SET DEFAULT gen_random_uuid();

-- Eksik kolonlar varsa
ALTER TABLE payments ADD COLUMN IF NOT EXISTS customer_id UUID;
ALTER TABLE payments ADD COLUMN IF NOT EXISTS amount INTEGER;
\`\`\`

VEYA:

📥 Settings (⚙️) sayfasından "🔧 Schema Otomatik Düzelt" butonuna tıklayın

✅ Bu işlemden sonra ödeme işlemleri çalışacaktır!

💡 NOT: Eski schema'da payment_method vardı, yeni schema'da method kullanılıyor.
            `.trim();

            console.error('🔴 SCHEMA ERROR DETECTED:', schemaError);
            console.log('\n📝 COPY-PASTE İÇİN SQL KOMUTLARI:');
            console.log('─'.repeat(50));
            console.log('-- Eski kolonu sil');
            console.log('ALTER TABLE payments DROP COLUMN IF EXISTS payment_method;');
            console.log('');
            console.log('-- Yeni kolonları ekle');
            console.log('ALTER TABLE payments ADD COLUMN IF NOT EXISTS method TEXT NOT NULL DEFAULT \'cash\';');
            console.log('ALTER TABLE payments ADD COLUMN IF NOT EXISTS notes TEXT;');
            console.log('ALTER TABLE payments ALTER COLUMN id SET DEFAULT gen_random_uuid();');
            console.log('─'.repeat(50));
            throw new Error(schemaError);
          }

          // Special message for missing columns in orders table
          if (data.error.includes('subtotal') || data.error.includes('tax')) {
            const missingColumn = data.error.includes('subtotal') ? 'subtotal' : 'tax';
            const schemaError = `
🔴 ORDERS TABLOSU GÜNCELLEME GEREKLİ!

${data.error}

⚡ 2 ÇÖZÜM SEÇENEĞİ:

📌 SEÇENEK 1: OTOMATİK DÜZELTME (ÖNERİLEN)
────────────────────────────────────
1️⃣ Settings (⚙️) sayfasını açın
2️⃣ "🔧 Schema Otomatik Düzelt" butonuna tıklayın
3️⃣ Gösterilen SQL komutlarını Supabase'de çalıştırın
4️⃣ Sayfayı yenileyin

📌 SEÇENEK 2: MANUEL SQL ÇALIŞTIRMA  
────────────────────────────────────────
1️⃣ Supabase SQL Editor: https://supabase.com/dashboard/project/_/sql
2️⃣ Aşağıdaki komutları çalıştırın:

-- Tüm eksik kolonları ekle
ALTER TABLE orders ADD COLUMN IF NOT EXISTS subtotal INTEGER NOT NULL DEFAULT 0;
ALTER TABLE orders ADD COLUMN IF NOT EXISTS tax INTEGER NOT NULL DEFAULT 0;

💡 NEDEN GEREKLİ?
Sisteminiz eski schema ile kurulmuş. Yeni özellikler için eksik kolonlar eklenmeli.

⚠️ İKİ KOLONU DA EKLEYİN! Yoksa başka hatalar alırsınız.
            `.trim();

            console.error('🔴 SCHEMA ERROR DETECTED:', schemaError);
            console.log('\n📝 COPY-PASTE İÇİN SQL KOMUTLARI:');
            console.log('─'.repeat(50));
            console.log('ALTER TABLE orders ADD COLUMN IF NOT EXISTS subtotal INTEGER NOT NULL DEFAULT 0;');
            console.log('ALTER TABLE orders ADD COLUMN IF NOT EXISTS tax INTEGER NOT NULL DEFAULT 0;');
            console.log('─'.repeat(50));
            throw new Error(schemaError);
          }

          // ⚠️ Special handling for duplicate phone number
          if (response.status === 409 || data.error.includes('duplicate key') || data.error.includes('zaten kayıtlı')) {
            console.error('⚠️ Duplicate customer:', data.error);
            // ✅ Preserve existingCustomer object in error
            const error: any = new Error(data.error);
            if (data.existingCustomer) {
              error.existingCustomer = data.existingCustomer;
            }
            throw error;
          }

          const schemaError = `
🔴 DATABASE SCHEMA ERROR!

${data.error}

📋 ÇÖZÜM ADIMLARI:
1. Settings (⚙️) sayfasını açın
2. "🗄️ PostgreSQL Schema İndir" butonuna tıklayın
3. İndirilen schema.sql dosyasını Supabase SQL Editor'a yükleyin
4. Sayfayı yenileyin ve tekrar deneyin

💡 Schema'nın tam ve doğru yüklendiğinden emin olun!
          `.trim();

          console.error('🔴 SCHEMA ERROR DETECTED:', schemaError);
          throw new Error(schemaError);
        }
      }

      // ✅ Security: Only show hint, not debug info with PINs
      if (data.hint) {
        console.warn('💡 Hint:', data.hint);
      }

      throw new Error(data.error || `API Error ${response.status}: ${response.statusText}`);
    }

    return data;
  } catch (error: any) {
    console.error('❌ fetchApi exception:', {
      endpoint,
      error: error.message,
      stack: error.stack
    });
    throw error;
  }
}

// Auth
export const authApi = {
  login: (pin: string, username: string) => fetchApi('/login', {
    method: 'POST',
    body: JSON.stringify({ pin: String(pin), username: String(username) }), // Force string types
  }),

  // Google ile giriş (yalnızca patron) — Google kimlik token'ı sunucuda doğrulanır
  googleLogin: (credential: string, tenantId?: string) => fetchApi('/auth/google-login', {
    method: 'POST',
    body: JSON.stringify({ credential, tenantId }),
  }),

  // Check if tenant exists (for login page)
  checkTenant: (tenantId: string) => fetchApi('/check-tenant', {
    method: 'POST',
    body: JSON.stringify({ tenantId }),
  }),

  // DEPRECATED: Don't use init from login, only from signup
  init: (tenantId: string, language?: string) => fetchApi('/init', {
    method: 'POST',
    body: JSON.stringify({ tenantId, language }),
  }),
};

// Users
export const usersApi = {
  getAll: () => fetchApi('/users'),
  create: (user: any) => fetchApi('/users', {
    method: 'POST',
    body: JSON.stringify(user),
  }),
  update: (id: string, updates: any) => fetchApi(`/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),
  delete: (id: string) => fetchApi(`/users/${id}`, { method: 'DELETE' }),
};

// Eleman çalışma saatleri
export const staffAvailabilityApi = {
  getAll: () => fetchApi('/staff-availability'),
  save: (userId: string, data: any) => fetchApi(`/staff-availability/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

// Customers
export const customersApi = {
  getAll: (page = 1, limit = 50, search = '', activeOnly = false) => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (activeOnly) params.append('active_only', 'true');
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    return fetchApi(`/customers?${params.toString()}${searchParam}`);
  },
  getAllWithBalances: (search = '') => {
    const searchParam = search ? `?search=${encodeURIComponent(search)}` : '';
    return fetchApi(`/customers/all-with-balances${searchParam}`);
  },
  search: (query: string) => fetchApi(`/customers/search?q=${encodeURIComponent(query)}`),
  create: (customer: any) => {
    const language = localStorage.getItem('language') || 'tr';
    return fetchApi('/customers', {
      method: 'POST',
      body: JSON.stringify({ ...customer, language }),
    });
  },
  update: (id: string, updates: any) => {
    const language = localStorage.getItem('language') || 'tr';
    return fetchApi(`/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...updates, language }),
    });
  },
  delete: (id: string) => fetchApi(`/customers/${id}`, { method: 'DELETE' }),
  getBalance: (id: string) => fetchApi(`/customers/${id}/balance`),
  getById: (id: string) => fetchApi(`/customers/${id}`),
  bulkFixBalances: () => fetchApi('/customers/bulk-fix-balances', { method: 'POST' }),
};

// Products — module-level cache (uygulama açık kaldığı sürece geçerli)
let _productsCache: { products: any[]; tenantId: string; at: number } | null = null;
const PRODUCTS_CACHE_TTL = 60_000; // 1 dk: SQL/ürün düzenlemesi ve çeviriler en geç bu sürede görünür

const fetchAllProducts = async (tenantId: string) => {
  const result = await fetchApi(`/products?page=1&limit=10000`);
  const products = result.products || [];
  // Boş sonuç önbelleğe alınmaz (geçici hata/oturum gecikmesinde ürünler kalıcı olarak boş kalmasın)
  if (products.length > 0) _productsCache = { products, tenantId, at: Date.now() };
  return result;
};

export const productsApi = {
  getAll: async (params?: { page?: number; limit?: number; search?: string; category?: string }) => {
    const tenantId = localStorage.getItem('tenant_id') || '';
    // limit: 10000 → "tüm ürünleri getir" çağrısı; taze önbellekten sun
    if (params?.limit && params.limit >= 10000) {
      if (_productsCache?.tenantId === tenantId && Date.now() - _productsCache.at < PRODUCTS_CACHE_TTL) {
        return { products: _productsCache.products };
      }
      return fetchAllProducts(tenantId);
    }
    const query = new URLSearchParams(params as any).toString();
    return fetchApi(`/products${query ? `?${query}` : ''}`);
  },
  /** Önbellekteki son liste (yaşı ne olursa olsun) — ekranı beklemeden doldurmak için */
  peekCache: (): any[] | null => {
    const tenantId = localStorage.getItem('tenant_id') || '';
    return _productsCache?.tenantId === tenantId ? _productsCache.products : null;
  },
  /** Önbelleği atlayıp sunucudan tazeler */
  refresh: () => fetchAllProducts(localStorage.getItem('tenant_id') || ''),
  clearCache: () => { _productsCache = null; },
  create: (product: any) => fetchApi('/products', {
    method: 'POST',
    body: JSON.stringify(product),
  }),
  update: (id: string, product: any) => fetchApi(`/products/${id}`, {
    method: 'PUT',
    body: JSON.stringify(product),
  }),
  batchUpdateOrder: (updates: { id: string; order: number }[]) => fetchApi('/products/batch-update-order', {
    method: 'POST',
    body: JSON.stringify({ updates }),
  }),
  delete: (id: string) => fetchApi(`/products/${id}`, { method: 'DELETE' }),
  deleteAll: () => fetchApi('/products/all/delete', { method: 'DELETE' }),
  cleanupNoIcon: () => fetchApi('/products/cleanup/no-icon', { method: 'DELETE' }),
  getPopularity: () => fetchApi('/products/stats/popularity'),
  loadDemo: () => fetchApi('/products/load-demo', { method: 'POST' }),
  resetTailoringPrices: () => fetchApi('/products/reset-tailoring-prices', { method: 'POST' }),
  uploadImage: (id: string, base64Image: string) => fetchApi(`/products/${id}/upload-image`, {
    method: 'POST',
    body: JSON.stringify({ base64Image }),
  }),
  deleteImage: (id: string, imageIndex: number) => fetchApi(`/products/${id}/images/${imageIndex}`, {
    method: 'DELETE',
  }),
};

// Orders
export const ordersApi = {
  getAll: async (params?: { status?: string; customerId?: string; startDate?: string; endDate?: string; loadAll?: boolean; page?: number; limit?: number; search?: string; searchType?: string; year?: string; excludeCancelled?: boolean; deliveryBranchId?: string; pickupBranchId?: string; branchId?: string; overdueDays?: number; sortOrder?: 'asc' | 'desc' }) => {
    // Convert boolean to string for URLSearchParams
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
    }
    const query = searchParams.toString();
    const data = await fetchApi(`/orders${query ? `?${query}` : ''}`);

    // Normalize orders array
    if (data.orders && Array.isArray(data.orders)) {
      return {
        ...data,
        orders: data.orders.map(normalizeOrder)
      };
    }
    return data;
  },
  getById: async (id: string) => {
    const data = await fetchApi(`/orders/${id}`);

    // Normalize single order
    if (data.order) {
      return {
        ...data,
        order: normalizeOrder(data.order)
      };
    }
    return data;
  },
  search: async (query: string) => {
    const data = await fetchApi(`/orders/search?q=${encodeURIComponent(query)}`);

    // Normalize orders array
    if (data.orders && Array.isArray(data.orders)) {
      return {
        ...data,
        orders: data.orders.map(normalizeOrder)
      };
    }
    return data;
  },
  create: (order: any) => fetchApi('/orders', {
    method: 'POST',
    body: JSON.stringify(order),
  }),
  updateStatus: (id: string, status: string) => fetchApi(`/orders/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  }),
  update: (id: string, updates: any) => fetchApi(`/orders/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),
  cancel: (id: string, cancelReason: string, cancelledBy?: string) => fetchApi(`/orders/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ cancelReason, cancelledBy }),
  }),
  migrateOrderNumbers: () => fetchApi('/orders/migrate/order-numbers', { method: 'POST' }),
  recalculateBalances: () => fetchApi('/orders/recalculate-balances', { method: 'POST' }),
  addDeliveryPhoto: (orderId: string, url: string) => fetchApi(`/orders/${orderId}/delivery-photos`, {
    method: 'POST',
    body: JSON.stringify({ url }),
  }),
  removeDeliveryPhoto: (orderId: string, removeIndex: number) => fetchApi(`/orders/${orderId}/delivery-photos`, {
    method: 'POST',
    body: JSON.stringify({ removeIndex }),
  }),
  uploadConditionPhoto: (base64Image: string) => fetchApi('/orders/upload-condition-photo', {
    method: 'POST',
    body: JSON.stringify({ base64Image }),
  }),
};

// Transactions (Cari Hesap)
export const transactionsApi = {
  getByCustomer: (customerId: string) => fetchApi(`/transactions/customer/${customerId}`),
};

// Payments (Müşteri ödemeleri)
export const paymentsApi = {
  create: (payment: { customerId: string; orderId?: string; amount: number; method: string; notes?: string }) =>
    fetchApi('/payments', {
      method: 'POST',
      body: JSON.stringify(payment),
    }),
  getByCustomer: (customerId: string) =>
    fetchApi(`/customers/${customerId}/payments`),
};

// Expenses
export const expensesApi = {
  getAll: (params?: { startDate?: string; endDate?: string }) => {
    const query = new URLSearchParams(params as any).toString();
    return fetchApi(`/expenses${query ? `?${query}` : ''}`);
  },
  create: (expense: any) => fetchApi('/expenses', {
    method: 'POST',
    body: JSON.stringify(expense),
  }),
  delete: (id: string) => fetchApi(`/expenses/${id}`, {
    method: 'DELETE',
  }),
};

// Expense Categories
export const expenseCategoriesApi = {
  getAll: () => fetchApi('/expense-categories'),
  create: (category: any) => fetchApi('/expense-categories', {
    method: 'POST',
    body: JSON.stringify(category),
  }),
  update: (id: string, category: any) => fetchApi(`/expense-categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(category),
  }),
  delete: (id: string) => fetchApi(`/expense-categories/${id}`, {
    method: 'DELETE',
  }),
  initDefaults: () => fetchApi('/expense-categories/init-defaults', {
    method: 'POST',
  }),
};

// Settings
export const settingsApi = {
  get: async () => {
    // Get settings from database
    const dbSettings = await fetchApi('/pg/settings');

    // Get custom settings from localStorage (for non-database fields)
    const customSettings = getCustomSettings();

    // Database fields that should NOT be overridden by localStorage
    const dbFields = [
      'company_name', 'companyName', 'header_info', 'headerInfo',
      'company_phone', 'companyPhone', 'company_address', 'companyAddress',
      'tax_rate', 'taxRate', 'logo_url', 'logoUrl',
      'receipt_footer', 'receiptFooter', 'receipt_note', 'receiptNote',
      'default_service_type', 'defaultServiceType', 'default_category', 'defaultCategory',
      'language', 'currency', 'currency_position', 'currencyPosition',
      'number_format', 'numberFormat', 'show_decimals', 'showDecimals',
      'date_format', 'dateFormat', 'delivery_days', 'deliveryDays',
      'work_on_sunday', 'workOnSunday', 'receipt_settings', 'receiptSettings',
      'social_media', 'socialMedia', 'whatsapp_templates', 'whatsappTemplates',
      'timezone'
    ];

    // Filter out database fields from customSettings
    const nonDbSettings: any = {};
    for (const key of Object.keys(customSettings)) {
      if (!dbFields.includes(key)) {
        nonDbSettings[key] = customSettings[key];
      }
    }

    return {
      ...dbSettings,
      settings: {
        ...dbSettings.settings,
        ...nonDbSettings
      }
    };
  },
  update: async (settings: any) => {
    // Separate database fields from custom fields
    const dbFields = [
      'company_name', 'header_info', 'company_phone', 'company_address',
      'instagram', 'facebook', 'website',
      'tax_rate', 'logo_url',
      'receipt_footer', 'receipt_note',
      'default_service_type', 'default_category',
      'language', 'currency', 'currency_position', 'currencyPosition',
      'number_format', 'numberFormat', 'show_decimals', 'showDecimals',
      'date_format', 'dateFormat', 'delivery_days', 'deliveryDays',
      'work_on_sunday', 'workOnSunday',
      'timezone'
    ];

    const dbSettings: any = {};
    const customSettings: any = {};

    for (const key of Object.keys(settings)) {
      if (dbFields.includes(key)) {
        dbSettings[key] = settings[key];
      } else if (key !== 'tenant_id' && key !== 'id' && key !== 'created_at' && key !== 'updated_at') {
        customSettings[key] = settings[key];
      }
    }

    // Save ALL settings to localStorage for caching
    const allSettingsForCache: any = {};
    for (const key of Object.keys(settings)) {
      if (key !== 'tenant_id' && key !== 'id' && key !== 'created_at' && key !== 'updated_at') {
        allSettingsForCache[key] = settings[key];
      }
    }
    saveCustomSettings(allSettingsForCache);

    // Fiş ayarlarından yalnız WhatsApp bağlantı tercihi sunucuya gider (sunucu mevcut receipt_settings ile birleştirir;
    // diğer fiş alanları eskisi gibi tarayıcıda kalır)
    const wrl = settings?.receipt_settings?.whatsappReceiptLink;
    if (typeof wrl === 'boolean') dbSettings.receipt_settings = { whatsapp_receipt_link: wrl };

    // Send only database fields to server
    const result = await fetchApi('/pg/settings', {
      method: 'PUT',
      body: JSON.stringify(dbSettings),
    });

    // Return merged settings
    return {
      ...result,
      settings: {
        ...result.settings,
        ...customSettings
      }
    };
  },
  deleteDemoData: () => fetchApi('/pg/settings/delete-demo-data', {
    method: 'DELETE',
  }),
  fixSchema: () => fetchApi('/pg/fix-schema', {
    method: 'POST',
  }),
  uploadLogo: (base64Image: string) => fetchApi('/settings/upload-logo', {
    method: 'POST',
    body: JSON.stringify({ base64Image }),
  }),
  debugSchema: () => fetchApi('/settings/debug-schema'),
};

// Helper functions for custom settings in localStorage
function getCustomSettings(): any {
  try {
    const stored = localStorage.getItem('customSettings');
    return stored ? JSON.parse(stored) : {};
  } catch (e) {
    console.error('Error reading custom settings from localStorage:', e);
    return {};
  }
}

function saveCustomSettings(settings: any): void {
  try {
    // Get existing custom settings
    const existing = getCustomSettings();
    // Merge with new settings
    const merged = { ...existing, ...settings };
    // Save back to localStorage
    localStorage.setItem('customSettings', JSON.stringify(merged));
  } catch (e) {
    console.error('Error saving custom settings to localStorage:', e);
  }
}

// Dashboard
export const dashboardApi = {
  getStats: async (period: string = 'today', branchId?: string | null) => {
    const tenantId = localStorage.getItem('tenantId');
    const storedBranchId = branchId !== undefined ? branchId : (localStorage.getItem('branchId') || '');
    const url = `${API_BASE}/dashboard/stats?period=${period}`;
    console.log(`📊 Dashboard fetching: ${url}`, storedBranchId ? `Branch: ${storedBranchId}` : 'All');
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`,
        ...(tenantId && { 'X-Tenant-ID': tenantId }),
        ...(storedBranchId && { 'X-Branch-ID': storedBranchId }),
      },
    });
    if (!response.ok) throw new Error('Dashboard fetch failed');
    const data = await response.json();
    console.log('📊 Dashboard raw data:', data);
    const s = data.stats || data;
    return {
      totalOrders: s.totalOrders || 0,
      totalRevenue: s.totalRevenue || 0,
      totalExpenses: s.totalExpenses || 0,
      totalPieces: s.totalPieces || s.serviceStats?.totalPieces || 0,
      cancelledOrdersCount: s.cancelledOrdersCount || 0,
      cancelledOrdersTotal: s.cancelledOrdersTotal || 0,
      ordersByStatus: s.ordersByStatus || {},
      stats: {
        totalOrders: s.totalOrders || 0,
        totalRevenue: s.totalRevenue || 0,
        totalExpenses: s.totalExpenses || 0,
        totalPieces: s.totalPieces || s.serviceStats?.totalPieces || 0,
        cancelledOrdersCount: s.cancelledOrdersCount || 0,
        cancelledOrdersTotal: s.cancelledOrdersTotal || 0,
        ordersByStatus: s.ordersByStatus || {},
      }
    };
  },
};

// Reports
export const reportsApi = {
  // Şube bazlı ciro — z-report-v3'ün branchStats'ını kullanır
  getBranchRevenue: async (startDate: string, endDate: string) => {
    const tenantId = localStorage.getItem('tenantId');
    const url = `https://gujyufrdumeossjsluuc.supabase.co/functions/v1/z-report-v3?startDate=${startDate}&endDate=${endDate}`;
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`,
        ...(tenantId && { 'X-Tenant-ID': tenantId }),
      },
    });
    if (!response.ok) throw new Error('Branch revenue fetch failed');
    const data = await response.json();
    return (data.branchStats || []) as Array<{
      branchId: string; branchName: string;
      revenue: number; orderCount: number;
      cash: number; card: number; bank: number; account: number;
    }>;
  },

  getZReport: async (date?: string, startDate?: string, endDate?: string, branchId?: string) => {
    const query = date
      ? `?date=${date}`
      : `?startDate=${startDate}&endDate=${endDate}`;
    const tenantId = localStorage.getItem('tenantId');
    const url = `https://gujyufrdumeossjsluuc.supabase.co/functions/v1/z-report-v3${query}`;
    console.log(`🌐 Z-Report Direct Fetch: ${url}`, branchId ? `Branch: ${branchId}` : 'All branches');
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`,
        ...(tenantId && { 'X-Tenant-ID': tenantId }),
        ...(branchId && { 'X-Branch-ID': branchId }),
      },
    });
    if (!response.ok) throw new Error('Z-Report fetch failed');
    return response.json();
  },
  getOrdersList: (startDate: string, endDate: string) => {
    return fetchApi(`/reports/orders-list?startDate=${startDate}&endDate=${endDate}`);
  },
  getDailyStats: (startDate: string, endDate: string) => {
    return fetchApi(`/orders/reports/daily-stats?startDate=${startDate}&endDate=${endDate}`);
  },
  getTopCustomers: (startDate: string, endDate: string, limit: number = 10) => {
    return fetchApi(`/orders/reports/top-customers?startDate=${startDate}&endDate=${endDate}&limit=${limit}`);
  },
};

// Invoices
export const invoicesApi = {
  getInvoices: () => fetchApi('/invoices'),
  getInvoiceById: (id: string) => fetchApi(`/invoices/${id}`),
  downloadInvoice: (id: string) => fetchApi(`/invoices/${id}/download`),
};

// Test Data (Performance Testing)
export const testDataApi = {
  generate: (tenantId: string, customerCount: number, orderCount: number) =>
    fetchApi('/init/test-data', {
      method: 'POST',
      body: JSON.stringify({ tenantId, customerCount, orderCount }),
    }),
  delete: () => fetchApi('/init/test-data', { method: 'DELETE' }),
};

// Debug API
export const debugApi = {
  getTenantInfo: () => fetchApi('/debug/tenant-info'),
  getHealth: () => fetchApi('/health'),
  testPaddle: () => fetchApi('/paddle-test'),
  increaseLimits: (customerIncrease?: number, receiptIncrease?: number) =>
    fetchApi('/admin/increase-limits', {
      method: 'POST',
      body: JSON.stringify({
        customerIncrease: customerIncrease || 100,
        receiptIncrease: receiptIncrease || 100
      }),
    }),
};

// Subscription API
export const subscriptionApi = {
  createPayment: (data: {
    tenantId: string;
    plan: 'starter' | 'professional' | 'enterprise';
    billingPeriod: 'monthly' | 'yearly';
    cardInfo: {
      cardHolderName: string;
      cardNumber: string;
      expireMonth: string;
      expireYear: string;
      cvc: string;
    };
    buyerInfo: {
      id?: string;
      name: string;
      surname: string;
      phone: string;
      email: string;
      identityNumber?: string;
      address: string;
      city?: string;
      country?: string;
      companyName?: string;
      ip?: string;
    };
  }) => fetchApi('/subscription/create-payment', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  chargeRecurring: (tenantId: string) => fetchApi('/subscription/charge-recurring', {
    method: 'POST',
    body: JSON.stringify({ tenantId }),
  }),

  updatePlan: (data: {
    tenantId: string;
    newPlan: 'starter' | 'professional' | 'enterprise';
    cardInfo?: {
      cardHolderName: string;
      cardNumber: string;
      expireMonth: string;
      expireYear: string;
      cvc: string;
    };
    buyerInfo?: {
      id?: string;
      name: string;
      surname: string;
      phone: string;
      email: string;
      identityNumber?: string;
      address: string;
      city?: string;
      country?: string;
      companyName?: string;
      ip?: string;
    };
  }) => fetchApi('/subscription/update-plan', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  getDetails: (tenantId: string) => fetchApi(`/subscription/details?tenantId=${tenantId}`),

  cancel: (tenantId: string) => fetchApi('/subscription/cancel', {
    method: 'POST',
    body: JSON.stringify({ tenantId }),
  }),

  // Coupon validation
  validateCoupon: (code: string, tenantId: string) => fetchApi('/subscription/validate-coupon', {
    method: 'POST',
    body: JSON.stringify({ code, tenantId }),
  }),

  // 🏦 Havale/EFT ile abonelik ödemesi (manuel onaylı) — referans no + banka bilgisi döner
  createBankTransfer: (tenantId: string, plan: string, billingCycle: 'monthly' | 'yearly', couponCode?: string) =>
    fetchApi('/subscription/bank-transfer', {
      method: 'POST',
      body: JSON.stringify({ tenantId, plan, billingCycle, couponCode }),
    }),

  // 🇹🇷 Iyzico 3D Secure Subscription Payments (Turkey only)
  iyzicoCreateSubscription: (data: {
    tenantId: string;
    plan: 'starter' | 'professional' | 'enterprise';
    billingPeriod: 'monthly' | 'yearly';
    billingInfo?: any;
    cardInfo: {
      cardHolderName: string;
      cardNumber: string;
      expireMonth: string;
      expireYear: string;
      cvc: string;
    };
    buyerInfo: {
      id?: string;
      name: string;
      surname: string;
      phone: string;
      email: string;
      identityNumber?: string;
      address: string;
      city?: string;
      country?: string;
      ip?: string;
      locale?: string;
    };
  }) => fetchApi('/payment/iyzico/subscription', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  iyzicoUpgradeSubscription: (data: {
    tenantId: string;
    newPlan: 'starter' | 'professional' | 'enterprise';
    billingInfo?: any;
    cardInfo: {
      cardHolderName: string;
      cardNumber: string;
      expireMonth: string;
      expireYear: string;
      cvc: string;
    };
    buyerInfo?: {
      id?: string;
      name: string;
      surname: string;
      phone: string;
      email: string;
      identityNumber?: string;
      address: string;
      city?: string;
      country?: string;
      ip?: string;
      locale?: string;
    };
  }) => fetchApi('/payment/iyzico/subscription-upgrade', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  iyzicoVerifySubscription: (data: {
    token: string;
  }) => fetchApi('/payment/iyzico/verify-subscription', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// Pricing API
export const pricingApi = {
  // Get all pricing configuration
  getPricing: () => fetchApi('/pricing'),

  // Initialize KV with default pricing (one-time setup)
  initializePricing: () => fetchApi('/pricing/initialize', {
    method: 'POST',
  }),

  // Force initialize (overwrite existing)
  forceInitializePricing: () => fetchApi('/pricing/initialize-force', {
    method: 'POST',
  }),

  // Admin endpoints
  updatePlanPricing: (plans: any) => fetchApi('/admin/pricing/plans', {
    method: 'PUT',
    body: JSON.stringify({ plans }),
  }),

  updateWhatsAppPricing: (whatsapp: any) => fetchApi('/admin/pricing/whatsapp', {
    method: 'PUT',
    body: JSON.stringify({ whatsapp }),
  }),

  resetPricing: () => fetchApi('/admin/pricing/reset', {
    method: 'POST',
  }),
};

// Billing Information API
export const billingApi = {
  saveBillingInfo: (data: {
    tenantId: string;
    billingInfo: {
      companyName: string;
      companyAddress: string;
      companyPhone: string;
      taxOffice: string;
      taxNumber: string;
      language: string;
    };
  }) => fetchApi('/billing/save', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  getBillingInfo: (tenantId: string) => fetchApi(`/billing/get?tenantId=${tenantId}`),

  getInvoices: (tenantId: string) => fetchApi(`/billing/invoices?tenantId=${tenantId}`),
};

// WhatsApp Credits API
export const whatsappCreditsApi = {
  getBalance: (tenantId: string) => fetchApi(`/whatsapp/credits?tenantId=${tenantId}`),

  getHistory: (tenantId: string) => fetchApi(`/whatsapp/credits-history?tenantId=${tenantId}`),

  purchaseCredits: (data: {
    tenantId: string;
    creditPackage: 'package1' | 'package2' | 'package3';
    cardInfo: {
      cardHolderName: string;
      cardNumber: string;
      expireMonth: string;
      expireYear: string;
      cvc: string;
    };
    buyerInfo: {
      id?: string;
      name: string;
      surname: string;
      phone: string;
      email: string;
      identityNumber?: string;
      address: string;
      city?: string;
      country?: string;
      companyName?: string;
      ip?: string;
    };
  }) => fetchApi('/whatsapp/purchase-credits', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// Tickets
export const ticketsApi = {
  // User endpoints
  create: (data: {
    subject: string;
    description: string;
    tenant_id: string;
    user_id?: string;
    user_name?: string;
  }) => fetchApi('/tickets', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  getByTenant: (tenantId: string) => fetchApi(`/tickets/${tenantId}`),

  addMessage: (ticketId: string, data: {
    message: string;
    user_id?: string;
    user_name?: string;
  }) => fetchApi(`/tickets/${ticketId}/messages`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // Legacy endpoints (compatibility)
  getAll: () => fetchApi('/tickets'),
  getAllForAdmin: () => fetchApi('/tickets/admin/all'),
  getById: (id: string) => fetchApi(`/tickets/${id}`),
  update: (id: string, data: {
    message?: string;
    senderId?: string;
    senderName?: string;
    isAdmin?: boolean;
    status?: string;
    rating?: number;
  }) => fetchApi(`/tickets/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  updateStatus: (id: string, status: string) => fetchApi(`/tickets/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  }),
  rate: (id: string, rating: number) => fetchApi(`/tickets/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ rating }),
  }),
  // Destek detay sayfası bu adla çağırıyor (yoktu, puanlama hata veriyordu)
  addRating: (id: string, rating: number) => fetchApi(`/tickets/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ rating }),
  }),
  delete: (id: string) => fetchApi(`/tickets/${id}`, {
    method: 'DELETE',
  }),

  // 🌍 Translation endpoints
  translate: (data: {
    text: string;
    targetLang: string;
    sourceLang?: string;
  }) => fetchApi('/tickets/translate', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  detectLanguage: (text: string) => fetchApi('/tickets/detect-language', {
    method: 'POST',
    body: JSON.stringify({ text }),
  }),
};

// WhatsApp
export const whatsappApi = {
  getConfig: () => fetchApi('/whatsapp/config'),
  saveConfig: (config: any) => fetchApi('/whatsapp/config', {
    method: 'POST',
    body: JSON.stringify(config),
  }),
  sendTestMessage: (phoneNumber: string, message: string) => fetchApi('/whatsapp/test', {
    method: 'POST',
    body: JSON.stringify({ phoneNumber, message }),
  }),
  // auto=true: kayıt/teslim yan etkisi olan otomatik gönderim (ücretsiz pakette günlük sınırda 402 yerine
  // 200 {success:false, skipped:'PLAN_LIMIT'} döner). Elle gönderimlerde verilmez.
  sendMessage: (phone: string, message: string, auto?: boolean) => fetchApi('/whatsapp/send', {
    method: 'POST',
    body: JSON.stringify({ phone, message, ...(auto ? { auto: true } : {}) }),
  }),
  sendOrderNotification: (phone: string, customerName: string, orderNumber: string, totalAmount: number, currency: string, auto?: boolean) => fetchApi('/whatsapp/order-notification', {
    method: 'POST',
    body: JSON.stringify({ phone, customerName, orderNumber, totalAmount, currency, ...(auto ? { auto: true } : {}) }),
  }),
  getStatus: () => fetchApi('/whatsapp/status'),
};

// SMS (NetGSM / Twilio) — credentials stored server-side, never in localStorage
export const smsApi = {
  getConfig: async () => {
    return fetchApi('/sms/config', { method: 'GET' });
  },

  saveConfig: async (config: any) => {
    return fetchApi('/sms/config', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  },

  sendTestMessage: async (phoneNumber: string, message: string) => {
    return fetchApi('/sms/test', {
      method: 'POST',
      body: JSON.stringify({ phoneNumber, message }),
    });
  },

  sendTestMessageV2: async (phoneNumber: string, message: string, provider: 'netgsm' | 'twilio') => {
    return fetchApi('/sms/test-v2', {
      method: 'POST',
      body: JSON.stringify({ phoneNumber, message, provider }),
    });
  },

  getStatus: async () => {
    return fetchApi('/sms/status', { method: 'GET' });
  },
};

// ============================================================
// BRANCHES API
// ============================================================
export const branchesApi = {
  getAll: () => fetchApi('/branches'),

  getById: (id: string) => fetchApi(`/branches/${id}`),

  create: (data: { name: string; address?: string; phone?: string }) =>
    fetchApi('/branches', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: { name?: string; address?: string; phone?: string; is_active?: boolean }) =>
    fetchApi(`/branches/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deactivate: (id: string) =>
    fetchApi(`/branches/${id}`, { method: 'DELETE' }),

  getStats: (id: string) => fetchApi(`/branches/${id}/stats`),
};
// ============================================================
// GELEN TALEPLER (temizim.app)
// ============================================================
export const serviceRequestsApi = {
  // { service_requests: [...devam eden işler], offer_requests: [...teklif verilebilecek], dry_cleaner_id }
  incoming: () => fetchApi('/service-requests/incoming'),
  incomingCount: () => fetchApi('/service-requests/incoming-count'),

  offer: (id: string, data: { item_prices: { item_id: string; unit_price: number; quantity: number }[]; note?: string; estimated_ready?: string }) =>
    fetchApi(`/service-requests/${id}/offer`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  reject: (id: string) =>
    fetchApi(`/service-requests/${id}/reject`, { method: 'POST' }),

  confirmPrices: (id: string, item_prices: { item_id: string; unit_price: number; quantity: number }[]) =>
    fetchApi(`/service-requests/${id}/prices`, {
      method: 'PATCH',
      body: JSON.stringify({ item_prices }),
    }),
};

export const inviteApi = {
  // Auth gerektirmez (anon) — signup öncesi önizleme
  check: async (code: string) => {
    const res = await fetch(`${API_BASE}/invite/check/${encodeURIComponent(code)}`, {
      headers: { 'Authorization': `Bearer ${publicAnonKey}` },
    });
    if (!res.ok) throw new Error('Kod geçersiz');
    return res.json();
  },
  // Tenant-aware (fetchApi X-Tenant-ID header'ı ekler)
  redeem: (code: string) =>
    fetchApi('/invite/redeem', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
};

/** GET /tenant/me — `access` eski sunucuda yoksa kısıtlama yok sayılır */
export interface TenantMe {
  subscriptionEndDate?: string | null;
  access?: Access;
  [key: string]: any;
}

export const tenantApi = {
  getMe: () => fetchApi<TenantMe>('/tenant/me'),
};

const CW_BASE = `https://${projectId}.supabase.co/functions/v1/carwash-df8bdc4f`;
async function cwFetch(path: string, options: RequestInit = {}) {
  const tenantId = localStorage.getItem('tenantId') || '';
  const sanitized = tenantId.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9-_]/g, '');
  const res = await fetch(`${CW_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${publicAnonKey}`,
      ...(sanitized && { 'X-Tenant-ID': sanitized }),
      // Fonksiyon bu token ile çağıranın firmasını doğrular (fetchApi ile aynı)
      ...(() => { const st = localStorage.getItem('sessionToken'); return st ? { 'X-Session-Token': safeHeader(st) } : {}; })(),
      ...options.headers,
    },
  });
  if (!res.ok) { const t = await res.text(); throw new Error(t.substring(0, 200)); }
  return res.json();
}

export const carWashApi = {
  // Slots
  getSlots: () => cwFetch('/slots'),
  createSlot: (data: { name: string; color?: string; sort_order?: number }) =>
    cwFetch('/slots', { method: 'POST', body: JSON.stringify(data) }),
  updateSlot: (id: string, data: { name?: string; color?: string; sort_order?: number }) =>
    cwFetch(`/slots/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSlot: (id: string) =>
    cwFetch(`/slots/${id}`, { method: 'DELETE' }),
  // Appointments
  getAppointments: (date?: string) =>
    cwFetch(`/appointments${date ? `?date=${date}` : ''}`),
  createAppointment: (data: any) =>
    cwFetch('/appointments', { method: 'POST', body: JSON.stringify(data) }),
  updateAppointment: (id: string, data: any) =>
    cwFetch(`/appointments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAppointment: (id: string) =>
    cwFetch(`/appointments/${id}`, { method: 'DELETE' }),
  // Settings
  getSettings: () => cwFetch('/settings'),
  saveSettings: (data: { working_days: number[]; work_start: string; work_end: string; holidays: string[] }) =>
    cwFetch('/settings', { method: 'PUT', body: JSON.stringify(data) }),
};

// ── SofaWash API ─────────────────────────────────────────────
const SW_BASE = `https://${projectId}.supabase.co/functions/v1/sofawash-df8bdc4f`;
async function swFetch(path: string, options: RequestInit = {}) {
  const tenantId = localStorage.getItem('tenantId') || '';
  const sanitized = tenantId.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9-_]/g, '');
  const res = await fetch(`${SW_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${publicAnonKey}`,
      ...(sanitized && { 'X-Tenant-ID': sanitized }),
      // Fonksiyon bu token ile çağıranın firmasını doğrular (fetchApi ile aynı)
      ...(() => { const st = localStorage.getItem('sessionToken'); return st ? { 'X-Session-Token': safeHeader(st) } : {}; })(),
      ...options.headers,
    },
  });
  if (!res.ok) { const t = await res.text(); throw new Error(t.substring(0, 200)); }
  return res.json();
}

export const sofaWashApi = {
  getSlots: () => swFetch('/slots'),
  createSlot: (data: { name: string; color?: string; sort_order?: number }) =>
    swFetch('/slots', { method: 'POST', body: JSON.stringify(data) }),
  updateSlot: (id: string, data: { name?: string; color?: string; sort_order?: number }) =>
    swFetch(`/slots/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSlot: (id: string) => swFetch(`/slots/${id}`, { method: 'DELETE' }),
  getAppointments: (date?: string) =>
    swFetch(`/appointments${date ? `?date=${date}` : ''}`),
  getOverdue: (before: string) => swFetch(`/appointments/overdue?before=${encodeURIComponent(before)}`),
  createAppointment: (data: any) =>
    swFetch('/appointments', { method: 'POST', body: JSON.stringify(data) }),
  updateAppointment: (id: string, data: any) =>
    swFetch(`/appointments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAppointment: (id: string) => swFetch(`/appointments/${id}`, { method: 'DELETE' }),
  getSettings: () => swFetch('/settings'),
  saveSettings: (data: any) => swFetch('/settings', { method: 'PUT', body: JSON.stringify(data) }),
};

// ── Barber API ────────────────────────────────────────────────
const BB_BASE = `https://${projectId}.supabase.co/functions/v1/barber-df8bdc4f`;
async function bbFetch(path: string, options: RequestInit = {}) {
  const tenantId = localStorage.getItem('tenantId') || '';
  const sanitized = tenantId.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9-_]/g, '');
  const res = await fetch(`${BB_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${publicAnonKey}`,
      ...(sanitized && { 'X-Tenant-ID': sanitized }),
      // Fonksiyon bu token ile çağıranın firmasını doğrular (fetchApi ile aynı)
      ...(() => { const st = localStorage.getItem('sessionToken'); return st ? { 'X-Session-Token': safeHeader(st) } : {}; })(),
      ...options.headers,
    },
  });
  if (!res.ok) { const t = await res.text(); throw new Error(t.substring(0, 200)); }
  return res.json();
}

export const barberApi = {
  getChairs: () => bbFetch('/chairs'),
  createChair: (data: { name: string; color?: string; sort_order?: number }) =>
    bbFetch('/chairs', { method: 'POST', body: JSON.stringify(data) }),
  updateChair: (id: string, data: { name?: string; color?: string; sort_order?: number }) =>
    bbFetch(`/chairs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteChair: (id: string) => bbFetch(`/chairs/${id}`, { method: 'DELETE' }),
  getAppointments: (date?: string) =>
    bbFetch(`/appointments${date ? `?date=${date}` : ''}`),
  getOverdue: (before: string) => bbFetch(`/appointments/overdue?before=${encodeURIComponent(before)}`),
  createAppointment: (data: any) =>
    bbFetch('/appointments', { method: 'POST', body: JSON.stringify(data) }),
  updateAppointment: (id: string, data: any) =>
    bbFetch(`/appointments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAppointment: (id: string) => bbFetch(`/appointments/${id}`, { method: 'DELETE' }),
  getSettings: () => bbFetch('/settings'),
  saveSettings: (data: any) => bbFetch('/settings', { method: 'PUT', body: JSON.stringify(data) }),
};
